import { Injectable } from '@angular/core';
import { AnalysisCase } from 'src/app/core/models/load/analysis/analysis-case.model';
import { AssemblyMovement } from 'src/app/core/models/load/analysis/assembly-movement.model';
import { MovementTransferType } from 'src/app/core/models/load/analysis/movement-transfer-type';
import { Movement } from 'src/app/core/models/load/analysis/movements.model';
import { DegreeOfFreedom } from 'src/app/core/models/load/degree-of-freedom.enum';
import { LoadingCase } from 'src/app/core/models/load/loading-case.model';
import { ParameterVariable } from 'src/app/core/models/load/parameter-variable.enum';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { IsEqual } from 'src/app/shared/utils/is-equal';
import { RemoveAllIds } from 'src/app/shared/utils/remove-all-ids/remove-all-ids';
import { DataSharingService } from '../../data-sharing/data-sharing.service';
import { MapsService } from '../objects/maps/maps.service';
import { TitleCopyService } from '../objects/title-copy/title-copy.service';
import { SimulationManagerService } from '../simulation-manager.service';
import { DynamicGeneralDTO } from './dynamic-general.dto';
import { DynamicParametersDTO } from './dynamic-parameters.dto';
import { LoadDTO } from './load.dto';

@Injectable({
  providedIn: 'root',
})
export class LoadService {
  constructor(
    private simulationService: SimulationManagerService,
    private titleCopyService: TitleCopyService,
    private mapsService: MapsService,
    private dataSharingService: DataSharingService,
  ) {}

  get simulation(): Simulation {
    return this.simulationService.simulation;
  }

  create(): LoadingCase {
    const newLoadingCase: LoadingCase = new LoadingCase();
    const id = this.simulationService.getNewId();
    newLoadingCase.internalId = id;
    newLoadingCase.currentId = -1;
    newLoadingCase.waveId = -1;
    newLoadingCase.nodeId = -1;
    newLoadingCase.relativeShipWaveAngle = 0;
    newLoadingCase.considerSecondOrderMov = false;
    newLoadingCase.raoFilePeak1 = null;
    newLoadingCase.raoFilePeak2 = null;
    newLoadingCase.variableToMaximize = ParameterVariable.ACCELERATION;
    newLoadingCase.degreeOfFreedom = DegreeOfFreedom.Heave;
    newLoadingCase.startTime = 100;
    newLoadingCase.stormTime = 10800;
    newLoadingCase.movements = this.getNewMovements();
    return newLoadingCase;
  }

  duplicate(loadCase: LoadingCase): LoadingCase {
    const prevId = loadCase.internalId;
    const newLoadCase: LoadingCase = new LoadingCase();
    const newId = this.simulationService.getNewId();
    Object.assign(newLoadCase, loadCase);
    newLoadCase.internalId = newId;
    newLoadCase.title = this.titleCopyService.getCopyFromTitle(loadCase.title || `LC-${prevId}`);
    RemoveAllIds(newLoadCase);
    return newLoadCase;
  }

  save(analysisCaseId: number, payload: LoadDTO[]): void {
    const analysisCase = this.getAnalysisById(analysisCaseId);
    if (!analysisCase || !payload) {
      return;
    }

    let hasChanges = false;
    analysisCase.loadingCase = payload.map((casePayload, index) => {
      hasChanges = !IsEqual(casePayload, analysisCase.loadingCase[index]) || hasChanges;
      const newLoadingCase = analysisCase.loadingCase[index] || this.create();
      Object.assign(newLoadingCase, casePayload);
      newLoadingCase.internalId = newLoadingCase.internalId || this.simulationService.getNewId();
      this.mapsService.updateObject(newLoadingCase);
      return newLoadingCase;
    });

    if (!hasChanges) {
      return;
    }

    this.mapsService.updateObject(analysisCase);
    this.simulationService.changeSimulation();
    this.dataSharingService.updateCatenary$.next();
    this.dataSharingService.localChanges$.next();
  }

  saveMovementsField(
    analysisId: number,
    movRefId: number,
    payload: number[][],
    fieldName: string,
  ): AnalysisCase {
    const analysis = this.getAnalysisById(analysisId);
    if (!analysis || !payload) {
      return;
    }

    let hasChanges = false;
    analysis.loadingCase.forEach((loadingCase, index) => {
      const mov = loadingCase.movements.find(({ referenceObjectId }) => {
        return movRefId === referenceObjectId;
      });

      if (mov && !IsEqual(mov[fieldName], payload[index])) {
        mov[fieldName] = payload[index];
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      return;
    }

    this.mapsService.updateObject(analysis);
    this.simulationService.changeSimulation(false);
    this.dataSharingService.localChanges$.next();
    return analysis;
  }

  saveDynamicGeneralData(analysisId: number, payload: DynamicGeneralDTO[]): AnalysisCase {
    const analysis = this.getAnalysisById(analysisId);
    if (!analysis || !payload) {
      return;
    }

    let hasChanges = this.saveInternalLoadCasesDynamicGeneral(analysis, payload);

    if (!hasChanges) {
      return;
    }

    this.mapsService.updateObject(analysis);
    this.simulationService.changeSimulation(false);
    this.dataSharingService.localChanges$.next();

    return analysis;
  }

  saveInternalLoadCasesDynamicGeneral(
    analysis: AnalysisCase,
    payload: DynamicGeneralDTO[],
  ): boolean {
    let hasChanges = false;
    analysis.loadingCase.forEach((loadingCase, index) => {
      if (loadingCase.raoFilePeak1 !== payload[index].raoFilePeak1) {
        loadingCase.raoFilePeak1 = payload[index].raoFilePeak1;
        hasChanges = true;
      }
      if (loadingCase.raoFilePeak2 !== payload[index].raoFilePeak2) {
        loadingCase.raoFilePeak2 = payload[index].raoFilePeak2;
        hasChanges = true;
      }
      if (loadingCase.considerSecondOrderMov !== payload[index].considerSecondOrderMov) {
        loadingCase.considerSecondOrderMov = payload[index].considerSecondOrderMov;
        hasChanges = true;
      }
    });
    return hasChanges;
  }

  saveDynamicParameters(analysisId: number, payload: DynamicParametersDTO[]): AnalysisCase {
    const analysis = this.getAnalysisById(analysisId);
    if (!analysis || !payload) {
      return;
    }

    const hasChanges = this.saveInternalLoadCasesDynamicParameters(analysis, payload);
    if (!hasChanges) {
      return;
    }
    this.mapsService.updateObject(analysis);
    this.simulationService.changeSimulation(false);
    this.dataSharingService.localChanges$.next();
    return analysis;
  }

  saveInternalLoadCasesDynamicParameters(
    analysis: AnalysisCase,
    payload: DynamicParametersDTO[],
  ): boolean {
    let hasChanges = false;
    analysis.loadingCase.forEach((loadingCase, index) => {
      if (loadingCase.nodeId !== payload[index].nodeId) {
        loadingCase.nodeId = payload[index].nodeId;
        hasChanges = true;
      }
      if (loadingCase.variableToMaximize !== payload[index].variableToMaximize) {
        loadingCase.variableToMaximize = payload[index].variableToMaximize;
        hasChanges = true;
      }
      if (loadingCase.degreeOfFreedom !== payload[index].degreeOfFreedom) {
        loadingCase.degreeOfFreedom = payload[index].degreeOfFreedom;
        hasChanges = true;
      }
      if (loadingCase.startTime !== payload[index].startTime) {
        loadingCase.startTime = payload[index].startTime;
        hasChanges = true;
      }
      if (loadingCase.stormTime !== payload[index].stormTime) {
        loadingCase.stormTime = payload[index].stormTime;
        hasChanges = true;
      }
    });
    return hasChanges;
  }

  getAnalysisById(id: number): AnalysisCase {
    const answer = this.simulation?.analysisCase?.find((obj) => obj.internalId === id);
    return answer || null;
  }

  getNewMovements(): Movement[] {
    const movements = [];
    this.simulation.floating.forEach((floating) => {
      movements.push(this.createMovement(floating.internalId, MovementTransferType.FLOATING));
    });
    this.simulation.node.forEach((node) => {
      movements.push(this.createMovement(node.internalId, MovementTransferType.NODE));
    });
    return movements;
  }

  createMovement(referenceObjectId: number, movementTransferType: MovementTransferType): Movement {
    const { internalId: dyncFuncId } = this.simulation.timeFunction[0];
    const { internalId: constFuncId } = this.simulation.timeFunction[1];
    const amplitudes = new Array(6).fill(0);
    const dynFuncIds = new Array(6).fill(dyncFuncId);
    const constFuncIds = new Array(6).fill(constFuncId);
    return {
      referenceObjectId,
      staticFunctionId: Clone(dynFuncIds),
      staticAmplitude: amplitudes,
      dynamicFunctionId: Clone(constFuncIds),
      dynamicAmplitude: amplitudes,
      movementTransferType,
    };
  }

  createAssemblyMovement(
    referenceObjectId: number,
    movementTransferType: MovementTransferType,
  ): AssemblyMovement {
    const { internalId: fId } = this.simulation.timeFunction[0];
    return {
      referenceObjectId,
      functionId: new Array(6).fill(fId),
      amplitude: new Array(6).fill(0),
      movementTransferType,
    };
  }

  deleteMovementsByReferenceId(id: number): void {
    this.simulation.analysisCase.forEach((analysisCase) => {
      analysisCase.loadingCase.forEach((loadingCase) => {
        loadingCase.movements = loadingCase.movements.filter(({ referenceObjectId }) => {
          return referenceObjectId !== id;
        });
      });
    });
  }

  resetRaosReferenceById(id: number): void {
    this.simulation.analysisCase.forEach((analysisCase) => {
      analysisCase.loadingCase.forEach((loadingCase) => {
        if (loadingCase.raoFilePeak1 === id) {
          loadingCase.raoFilePeak1 = -1;
        }
        if (loadingCase.raoFilePeak2 === id) {
          loadingCase.raoFilePeak2 = -1;
        }
      });

      this.mapsService.updateObject(analysisCase);
    });

    this.simulationService.changeSimulation(false);
  }
}
